// ONBOARDING-P3: the case detail's Seating tab, the runner. "Deploy seats" plans the run over the
// seats the owner approved (`onboarding.seating.deploy`), then runs them one step at a time
// (`onboarding.seating.next`, one request in flight, until nothing moves, capped at four calls a
// seat), updating each seat's status and step after every answer. A failed call is not the end
// of the run: the server marks that seat failed (its step kept), so the tab rereads the run and
// carries on; "Deploy seats" again resumes a failed seat where it stopped. "Continue" resumes
// seats waiting for their set-up.
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, Notice } from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../ops-parts";
import { LoadError, Loading, Section } from "../screen-parts";
import { SEATING_STATUS } from "./onboarding-labels";

type Seating = OperationOutput<"onboarding.seating.get">;

/** The recovery contract's NEXT step of a seat, in plain words. */
const STEP: Record<string, string> = {
  seat: "next: open the seat",
  de: "next: create the digital employee",
  provision: "next: set up",
  connect: "next: connect to its runtime",
  activate: "next: go live",
  first_task: "next: deliver its first task",
  ready: "done",
};

/** The six handover checks, in plain words. */
const CHECK: Record<string, string> = {
  owner_access: "Owner signed up",
  seats: "Approved seats deployed and held",
  spec: "Digital employees run their spec",
  connections: "Connections usable",
  intake: "Intake readable by the digital employees",
  first_task: "First task delivered",
};

export const readinessKey = (co: string) => ["onboarding", "readiness", co] as const;

export const seatingKey = (co: string) => ["onboarding", "seating", co] as const;

function allowanceLine(a: Seating["allowance"]): string {
  const plan = a.plan_name ?? a.plan_key ?? "No plan";
  if (a.included_des === null)
    return `Plan ${plan} sets no limit on digital employees, ${a.in_use} in use`;
  const held = a.reserved > 0 ? `, ${a.reserved} held for seats being set up` : "";
  return `Plan ${plan} includes ${a.included_des} digital employee${a.included_des === 1 ? "" : "s"}, ${a.in_use} in use${held}`;
}

/** Readiness (the six checks) and the handover that ends onboarding. */
function ReadinessSection({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: readinessKey(co),
    queryFn: () => api.client.onboarding.readiness.check({ params: { co } }),
  });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  async function act(handover: boolean) {
    setBusy(true);
    setProblem(null);
    try {
      const v = handover
        ? await api.client.onboarding.handover({ params: { co } })
        : await api.client.onboarding.readiness.check({ params: { co } });
      qc.setQueryData(readinessKey(co), v);
      if (handover) await qc.invalidateQueries({ queryKey: ["onboarding"] });
    } catch (e) {
      setProblem(
        problemText(e, handover ? "The handover failed." : "Readiness could not be checked."),
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Section id="seating-readiness" title="Readiness and handover">
      {q.isPending ? (
        <Loading label="Checking readiness" />
      ) : q.isError ? (
        <LoadError what="Readiness" error={q.error} />
      ) : (
        <div className="flex flex-col gap-3" data-slot="onboarding-readiness">
          <ul className="flex flex-col gap-1">
            {q.data.checks.map((c) => (
              <li key={c.key} data-check={c.key} data-ok={c.ok} className="flex flex-wrap gap-2">
                <Badge variant={c.ok ? "success" : "warning"}>{c.ok ? "Holds" : "Not yet"}</Badge>
                <span className="font-medium">{CHECK[c.key] ?? c.key}</span>
                <span className="text-sm text-muted-foreground">{c.detail}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            {q.data.handover_at ? (
              <span data-slot="readiness-handed-over">
                Handed over {new Date(q.data.handover_at).toLocaleString()}: onboarding has ended
                and the setup assistant no longer has access.
              </span>
            ) : (
              <>
                <Button variant="secondary" disabled={busy} onClick={() => void act(false)}>
                  Check again
                </Button>
                <Button disabled={busy || !q.data.ready_at} onClick={() => void act(true)}>
                  Hand over
                </Button>
                {!q.data.ready_at ? (
                  <span className="text-sm text-muted-foreground">
                    Every check must hold before the handover.
                  </span>
                ) : null}
              </>
            )}
          </div>
          {problem ? (
            <Notice tone="danger" role="alert">
              {problem}
            </Notice>
          ) : null}
        </div>
      )}
    </Section>
  );
}

export function SeatingTab({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: seatingKey(co),
    queryFn: () => api.client.onboarding.seating.get({ params: { co } }),
  });
  const [running, setRunning] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  async function run(plan: boolean) {
    setRunning(true);
    setProblems([]);
    const put = (v: Seating) => qc.setQueryData(seatingKey(co), v);
    try {
      let v = plan
        ? await api.client.onboarding.seating.deploy({ params: { co } })
        : await api.client.onboarding.seating.get({ params: { co } });
      put(v);
      // Each call moves one step of one seat (a DE seat has up to six: created, its set-up asked
      // for, set up, its connection waited on, connected, live); the run stops when nothing
      // could move.
      const cap = v.pending * 6 + 1;
      for (let calls = 0; calls < cap && v.pending > 0; calls++) {
        try {
          v = await api.client.onboarding.seating.next({ params: { co } });
        } catch (e) {
          setProblems((p) => [...p, problemText(e, "A seat could not be run.")]);
          v = await api.client.onboarding.seating.get({ params: { co } });
        }
        put(v);
        if (!v.ran) break;
      }
    } catch (e) {
      setProblems((p) => [...p, problemText(e, "The seats could not be deployed.")]);
    } finally {
      setRunning(false);
    }
  }

  if (q.isPending) return <Loading label="Loading seating" />;
  if (q.isError) return <LoadError what="Seating" error={q.error} />;
  const v = q.data;
  const open = v.tickets.filter((t) => t.status === "open");
  return (
    <div className="flex flex-col gap-4" data-slot="onboarding-seating">
      <div className="flex flex-col gap-1">
        <p data-slot="seating-allowance">{allowanceLine(v.allowance)}.</p>
        <p className="text-sm text-muted-foreground" data-slot="seating-approved">
          {v.sent_version === null
            ? "No proposal was sent yet."
            : `${v.approved} seat${v.approved === 1 ? "" : "s"} approved by the owner on version ${v.sent_version}`}
          {v.pending > 0 ? ` · ${v.pending} pending` : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={!v.can_deploy || running} onClick={() => void run(true)}>
          {running ? "Deploying…" : "Deploy seats"}
        </Button>
        {v.pending > 0 && !running ? (
          <Button variant="secondary" onClick={() => void run(false)}>
            Continue
          </Button>
        ) : null}
        {v.waiting > 0 ? (
          <span className="text-sm text-muted-foreground" data-slot="seating-waiting">
            {v.waiting} digital employee{v.waiting === 1 ? " is" : "s are"} being set up or
            connected: continue shortly.
          </span>
        ) : null}
        {!v.can_deploy && v.blocked_reason ? (
          <span className="text-sm text-muted-foreground" data-slot="seating-blocked">
            {v.blocked_reason}
          </span>
        ) : null}
      </div>
      {problems.map((p, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: an append-only list of messages
        <Notice key={i} tone="danger" role="alert">
          {p}
        </Notice>
      ))}
      {v.seats.length ? (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {v.seats.map((s) => {
            const st = SEATING_STATUS[s.status] ?? { label: s.status, variant: "neutral" as const };
            return (
              <li
                key={s.seat_key}
                data-seat={s.seat_key}
                data-status={s.status}
                className="flex flex-col gap-1 px-3 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{s.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {s.kind === "de" ? "Digital employee" : "Person"}
                    {s.template_key ? ` · ${s.template_key}` : ""}
                    {` · ${STEP[s.step] ?? s.step}`}
                    {s.attempts > 0 ? ` · ${s.attempts} of 3 tries failed` : ""}
                  </span>
                  <Badge variant={st.variant} className="ml-auto">
                    {st.label}
                  </Badge>
                </div>
                {s.message ? <p className="text-sm text-muted-foreground">{s.message}</p> : null}
                {s.last_error ? (
                  <p className="text-sm text-destructive">Last error: {s.last_error}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No seats planned yet. Deploy seats plans the run over the approved seats.
        </p>
      )}
      <Section id="seating-tickets" title="Template tickets">
        {open.length ? (
          <ul className="flex flex-col gap-3" data-slot="seating-tickets">
            {open.map((t) => (
              <li key={t.id} className="rounded-md border border-border px-3 py-2">
                <p className="font-medium">{t.title}</p>
                {t.kpis.length ? (
                  <p className="text-sm">
                    <span className="text-muted-foreground">KPIs: </span>
                    {t.kpis.join(" · ")}
                  </p>
                ) : null}
                {t.first_goals.length ? (
                  <p className="text-sm">
                    <span className="text-muted-foreground">First goals: </span>
                    {t.first_goals.join(" · ")}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No open template tickets.</p>
        )}
      </Section>
      <ReadinessSection api={api} co={co} />
    </div>
  );
}
